import {
  type CoordinatesParamsType,
  type NeedsParamsType,
  type StepsSelectionParamsType,
  type TravelModeType,
  coordinatesParser,
  needsParamsParser,
  stepsSelectionSchemaParser,
  travelModeParamsParser,
} from '@dolcevia/lib/searchParams';
import type { OpenRouteResponse } from '@dolcevia/types/openRouteResponse.d';
import { useQuery } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { useQueryStates } from 'nuqs';
import { getCyclingType, getDisplayedSteps } from './route/utils';

export interface RouteHookProps {
  coordinatesOverride?: CoordinatesParamsType;
  needsOverride?: NeedsParamsType;
  travelModeOverride?: TravelModeType;
  stepsSelectionOverride?: StepsSelectionParamsType;
}

export const useRoute = ({
  coordinatesOverride,
  needsOverride,
  travelModeOverride,
  stepsSelectionOverride,
}: RouteHookProps) => {
  const pathName = usePathname();
  const [{ coordinates, needs, travelMode, stepsSelection }] = useQueryStates({
    coordinates: coordinatesParser,
    needs: needsParamsParser,
    travelMode: travelModeParamsParser,
    stepsSelection: stepsSelectionSchemaParser,
  });

  const params = {
    coordinates: coordinatesOverride ?? coordinates,
    needs: needsOverride ?? needs,
    travelMode: travelModeOverride ?? travelMode,
    stepsSelection: stepsSelectionOverride ?? stepsSelection,
  };
  const cyclingType = getCyclingType(
    params.needs.bikeType,
    params.travelMode?.mode,
  );
  const { data, isFetching } = useQuery({
    queryKey: [
      'routes',
      cyclingType,
      params.coordinates,
      pathName === '/recap' && params.stepsSelection,
    ],
    queryFn: async () => {
      const displayedSteps = getDisplayedSteps(
        pathName,
        params.coordinates,
        params.stepsSelection,
      );
      const data = await fetch('/api/route', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates: displayedSteps,
          cyclingType,
        }),
        cache: 'force-cache',
      });
      const response = (await data.json()) as OpenRouteResponse | undefined;
      return response;
    },
    enabled:
      !!params.coordinates &&
      params.coordinates?.length > 1 &&
      params.coordinates.every(
        (coordinate) => !!coordinate.lat && !!coordinate.lng,
      ),
    retry: false,
  });
  return {
    isFetching,
    data,
  };
};
